/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateSalesOrderRequest } from '../models/create-sales-order-request';
import { GetSalesOrderResponse } from '../models/get-sales-order-response';
import { Language } from '../models/language';
import { SalesOrderResponse } from '../models/sales-order-response';
import { UpdateSalesOrderRequest } from '../models/update-sales-order-request';

/**
 * Sales order APIs
 */
@Injectable({ providedIn: 'root' })
export class SalesOrdersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllSalesOrders()` */
  static readonly GetAllSalesOrdersPath = '/api/v1/sales-orders';

  /**
   * Get list of sales order.
   *
   * Gets the list of sales orders.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllSalesOrders()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllSalesOrders$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - contract number
       *   - from date
       *   - to date
       *   - flying hours
       *   - remaining flying hours
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from?: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to?: string;

      /**
       * This enables filtering of sales order based on the client id
       */
      clientId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetSalesOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SalesOrdersService.GetAllSalesOrdersPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('clientId', params.clientId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetSalesOrderResponse>;
        })
      );
  }

  /**
   * Get list of sales order.
   *
   * Gets the list of sales orders.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllSalesOrders$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllSalesOrders(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - contract number
       *   - from date
       *   - to date
       *   - flying hours
       *   - remaining flying hours
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from?: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to?: string;

      /**
       * This enables filtering of sales order based on the client id
       */
      clientId?: number;
    },
    context?: HttpContext
  ): Observable<GetSalesOrderResponse> {
    return this.getAllSalesOrders$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetSalesOrderResponse>): GetSalesOrderResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createSalesOrder()` */
  static readonly CreateSalesOrderPath = '/api/v1/sales-orders';

  /**
   * Create new sales order.
   *
   * Create a new sales item to the backend.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createSalesOrder()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSalesOrder$Response(
    params: {
      body: CreateSalesOrderRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<SalesOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SalesOrdersService.CreateSalesOrderPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<SalesOrderResponse>;
        })
      );
  }

  /**
   * Create new sales order.
   *
   * Create a new sales item to the backend.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createSalesOrder$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createSalesOrder(
    params: {
      body: CreateSalesOrderRequest;
    },
    context?: HttpContext
  ): Observable<SalesOrderResponse> {
    return this.createSalesOrder$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<SalesOrderResponse>): SalesOrderResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getSalesOrderById()` */
  static readonly GetSalesOrderByIdPath = '/api/v1/sales-orders/{id}';

  /**
   * fetch sales order details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSalesOrderById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSalesOrderById$Response(
    params: {
      /**
       * The sales order id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<SalesOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SalesOrdersService.GetSalesOrderByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<SalesOrderResponse>;
        })
      );
  }

  /**
   * fetch sales order details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSalesOrderById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSalesOrderById(
    params: {
      /**
       * The sales order id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<SalesOrderResponse> {
    return this.getSalesOrderById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<SalesOrderResponse>): SalesOrderResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `updateSalesOrderById()` */
  static readonly UpdateSalesOrderByIdPath = '/api/v1/sales-orders/{id}';

  /**
   * update sales order item.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateSalesOrderById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSalesOrderById$Response(
    params: {
      /**
       * The sales order id that needs to be update
       */
      id: number;
      body: UpdateSalesOrderRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<SalesOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SalesOrdersService.UpdateSalesOrderByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<SalesOrderResponse>;
        })
      );
  }

  /**
   * update sales order item.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateSalesOrderById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSalesOrderById(
    params: {
      /**
       * The sales order id that needs to be update
       */
      id: number;
      body: UpdateSalesOrderRequest;
    },
    context?: HttpContext
  ): Observable<SalesOrderResponse> {
    return this.updateSalesOrderById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<SalesOrderResponse>): SalesOrderResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getSalesOrderSummeryReportById()` */
  static readonly GetSalesOrderSummeryReportByIdPath =
    '/api/v1/sales-orders/{id}/reports';

  /**
   * fetch sales order summery report.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSalesOrderSummeryReportById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSalesOrderSummeryReportById$Response(
    params: {
      /**
       * The sales order id that needs to be fetched
       */
      id: number;

      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to: string;

      /**
       * To include Flight Time
       */
      flightTime?: boolean;

      /**
       * To include Layover Time
       */
      layoverTime?: boolean;

      /**
       * To include Waiting Time
       */
      waitingTime?: boolean;

      /**
       * To include Dead Head
       */
      deadHead?: boolean;

      /**
       * To include Language
       */
      language?: Language;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      SalesOrdersService.GetSalesOrderSummeryReportByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('flightTime', params.flightTime, {});
      rb.query('layoverTime', params.layoverTime, {});
      rb.query('waitingTime', params.waitingTime, {});
      rb.query('deadHead', params.deadHead, {});
      rb.query('language', params.language, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'blob', accept: 'application/*', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * fetch sales order summery report.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSalesOrderSummeryReportById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSalesOrderSummeryReportById(
    params: {
      /**
       * The sales order id that needs to be fetched
       */
      id: number;

      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to: string;

      /**
       * To include Flight Time
       */
      flightTime?: boolean;

      /**
       * To include Layover Time
       */
      layoverTime?: boolean;

      /**
       * To include Waiting Time
       */
      waitingTime?: boolean;

      /**
       * To include Dead Head
       */
      deadHead?: boolean;

      /**
       * To include Language
       */
      language?: Language;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.getSalesOrderSummeryReportById$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }
}
